import VenueBasedTicketBasketRepository from "@app-contexts/dashboard/features/box-office/basket/data/repositories/VenueBasedTicketBasketRepository";
import PublicBasketRepository from "@app-contexts/public/features/basket/data/repositories/PublicBasketRepository";
import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";
import {
  common,
  enums,
  enumsTypes,
  ITEM_STATUS_OPTIONS,
} from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import { X_SP_UUID } from "@shared/env";
import PublicBasedVenuesRepository from "@shared/modules/venue/repositories/PublicBasedVenuesRepository";
import {
  CALENDAR_CACHE_PREFIX,
  CALENDAR_EVENTS_PREFIX,
} from "@shared/components/ui-kit/data-display/Calendar/caching/usePublicCalendarEventsV2";
import axios, { AxiosError } from "axios";
import _ from "lodash";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import uniq from "lodash/uniq";
import { QueryClient } from "react-query";
import VenueBasedTicketBasketHoldsRepository from "@app-contexts/dashboard/features/box-office/holds/data/repositories/VenueBasedTicketBasketHoldsRepository";
import UserBasedTicketBasketHoldRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketHoldRepository";
import PublicBasedTTLocationPermissionsRepository from "../../seating/repositories/PublicBasedTTLocationPermissionsRepository";
import PublicBasedTTLocationPermissionsV2Repository from "../../seating/repositories/PublicBasedTTLocationPermissionsV2Repository";
import PublicBasedTTSeatPermissionsRepository from "../../seating/repositories/PublicBasedTTSeatPermissionsRepository";
import PublicBasedTTSeatPermissionsV2Repository from "../../seating/repositories/PublicBasedTTSeatPermissionsV2Repository";
import { HandlesBasketRequests } from "../contracts";
import {
  CustomPackageParentType,
  Donation,
  GiftCard,
  ItemGroup,
  LocationPermission,
  MembershipLevel,
  ProductAttribute,
  SeatPermission,
  SeatWaitlist,
  TicketBasket,
  TicketBasketCustomSubTicketType,
  TicketBasketType,
  TicketType,
} from "../types";
import { TicketItemGroupType } from "../types/common/ticket-item-group/ticket-item-group-type";

// public repos to invalidate
const publicBaseEventRepository = new PublicBaseEventRepository();
const publicBasedVenuesRepository = new PublicBasedVenuesRepository();

// seating repos to invalidate (used on both public and venue)
const publicBasedTTSeatPermissionsRepository =
  new PublicBasedTTSeatPermissionsRepository();
const publicBasedTTSeatPermissionsV2Repository =
  new PublicBasedTTSeatPermissionsV2Repository();
const publicBasedTTLocationPermissionsRepository =
  new PublicBasedTTLocationPermissionsRepository();
const publicBasedTTLocationPermissionsV2Repository =
  new PublicBasedTTLocationPermissionsV2Repository();

// Invalidate seating react queries
async function invalidateSeatingQueries(queryClient: QueryClient) {
  await queryClient.invalidateQueries(
    publicBasedTTSeatPermissionsRepository.getRepositoryName()
  );
  await queryClient.invalidateQueries(
    publicBasedTTSeatPermissionsV2Repository.getRepositoryName()
  );
  await queryClient.invalidateQueries(
    publicBasedTTLocationPermissionsRepository.getRepositoryName()
  );
  await queryClient.invalidateQueries(
    publicBasedTTLocationPermissionsV2Repository.getRepositoryName()
  );
}

// Invalidate PUBLIC react queries
// This refetches info on public pages so that updated statuses are shown to users
async function invalidatePublicQueries(queryClient: QueryClient) {
  invalidateSeatingQueries(queryClient);
  // invalidate more queries here if needed (ex: memberships, products, etc.)
  await queryClient.invalidateQueries("event");
  await queryClient.invalidateQueries(
    publicBaseEventRepository.getRepositoryName()
  );
  await queryClient.invalidateQueries(
    publicBasedVenuesRepository.getRepositoryName()
  );
  // Invalidate all calendar cache queries to ensure fresh event data
  await queryClient.invalidateQueries(CALENDAR_CACHE_PREFIX);
  await queryClient.invalidateQueries(CALENDAR_EVENTS_PREFIX);
}

async function invalidateQueries(type: DataProvider, queryClient: QueryClient) {
  switch (type) {
    case DataProvider.Public:
      return invalidatePublicQueries(queryClient);
    // Add invalidation logic for other data providers here if needed
    default:
      return undefined;
  }
}

/**
 * Invalidate calendar and event queries after successful basket operations.
 * This ensures users see updated event availability after adding/removing items.
 */
export async function invalidateQueriesOnBasketSuccess(
  type: DataProvider,
  queryClient: QueryClient
) {
  // For now, use the same invalidation logic as errors since we want to
  // refresh the same queries (events and calendar data)
  return invalidateQueries(type, queryClient);
}

/**
 * If the error is because all items are in other user's baskets,
 * invalidate the event query to get the latest event and ticket type data
 */
export async function invalidateQueriesOnBasketError(
  type: DataProvider,
  queryClient: QueryClient,
  error: AxiosError | unknown
) {
  if (
    axios.isAxiosError(error) &&
    Array.isArray(error?.response?.data?.error_code) &&
    error?.response?.data?.error_code?.includes(
      enums.TICKETS_VALIDATION_ERROR_MAP.TVEM_UNFULFILLABLE_LINE_ITEM.id
    )
  ) {
    await invalidateQueries(type, queryClient);
  }
}

/**
 * Convert an array of items into item groups for the backend.
 * This is used when creating a basket in our hook file in
 * conjunction with any item groups from an existing basket.
 */
export function convertToItemGroups(
  items: Array<{
    item: unknown;
    quantity: number;
    parent?: number | null;
    pay_what_you_can_price?: string | null;
    is_upgraded?: boolean;
    // TODO: should refactor to always pass itemType since the shape of an item is inconsistent across endpoints
    itemType?: enumsTypes.ItemTypesMapIdType;
  }>
): Array<TicketItemGroupType> {
  return items.reduce<Array<TicketItemGroupType>>(
    (
      itemGroups,
      { item, quantity, parent, pay_what_you_can_price, is_upgraded, itemType }
    ) => {
      if (isCustomPackageParent(item)) {
        itemGroups.push({
          quantity,
          type: item.parentTicketType,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
          accepted_terms: item.accepted_terms,
          is_upgraded,
        });
      }

      if (isTicketType(item)) {
        itemGroups.push({
          quantity,
          type: item.id,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
          accepted_terms: item.accepted_terms,
          parent,
          pay_what_you_can_price,
          is_upgraded,
        });
      }

      // TODO: Add another condition if we want to support waitlisted memberships.
      // This is just for ticket types.
      if (isSeatWaitlist(item)) {
        itemGroups.push({
          quantity,
          type: item.itemTypeId,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
          tt_seat_permissions: [],
          waitlist_seat_segment: item.waitlist_seat_segment,
          is_upgraded,
        });
      }

      if (
        isSeatPermission(item) &&
        item.mapItemType === common.SeatingMapItemType.MEMBERSHIP
      ) {
        itemGroups.push({
          quantity,
          membership_level: item.itemTypeId,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
          tt_seat_permissions: [item.permissionId],
          parent: item.itemGroupParent,
          accepted_terms: item.accepted_terms,
          is_upgraded,
        });
      }

      if (
        isSeatPermission(item) &&
        item.mapItemType === common.SeatingMapItemType.EVENT
      ) {
        itemGroups.push({
          quantity,
          type: item.itemTypeId,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
          tt_seat_permissions: [item.permissionId],
          parent: item.itemGroupParent,
          accepted_terms: item.accepted_terms,
          pay_what_you_can_price,
          is_upgraded,
        });
      }

      if (
        isLocationPermission(item) &&
        item.mapItemType === common.SeatingMapItemType.MEMBERSHIP
      ) {
        itemGroups.push({
          quantity,
          membership_level: item.itemTypeId,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
          tt_location_permissions: [item.permissionId],
          parent: item.itemGroupParent,
          accepted_terms: item.accepted_terms,
          is_upgraded,
        });
      }

      if (
        isLocationPermission(item) &&
        item.mapItemType === common.SeatingMapItemType.EVENT
      ) {
        itemGroups.push({
          quantity,
          type: item.itemTypeId,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
          tt_location_permissions: [item.permissionId],
          parent: item.itemGroupParent,
          accepted_terms: item.accepted_terms,
          pay_what_you_can_price,
          is_upgraded,
        });
      }

      if (isMembershipLevelItemType(itemType) || isMembershipLevel(item)) {
        itemGroups.push({
          quantity,
          renewal_member: (item as MembershipLevel).renewal_id,
          membership_level:
            (item as MembershipLevel)?.membership_level ||
            (item as MembershipLevel).id,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
          is_upgraded,
        });
      }

      if (isProductAttributeItemType(itemType) || isProductAttribute(item)) {
        itemGroups.push({
          quantity,
          product_attribute: (item as ProductAttribute).id,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
          is_upgraded,
        });
      }

      if (isDonationItemType(itemType) || isDonation(item)) {
        itemGroups.push({
          quantity: 1,
          charity_id: (item as Donation).id,
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
          donation_amount: (item as Donation).donation_amount,
          is_upgraded,
        });
      }

      if (isGiftCardItemType(itemType) || isGiftCard(item)) {
        itemGroups.push({
          quantity,
          gift_card: (item as GiftCard).id,
          gift_card_denomination: (item as GiftCard).denomination,
          gift_card_recipient: {
            custom_message: (item as GiftCard).custom_message,
            notify_on: (item as GiftCard).notify_on,
            recipient_email: (item as GiftCard).recipient_email,
            recipient_name: (item as GiftCard).recipient_name,
          },
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_GIFT_CARD.id,
          is_upgraded,
        });
      }

      if (isItemGroup(item)) {
        itemGroups.push({
          ...item,
          quantity,
          is_upgraded,
        });
      }

      // Add additional item types as required...

      return itemGroups;
    },
    []
  );
}

/**
 * Persist the given basket to the backend.
 * Note: This function no longer handles queueing - that is now handled in useBasket.ts
 */
export async function persist(
  item_groups: Array<TicketItemGroupType>,
  type: DataProvider,
  config: {
    basket?: TicketBasket;
    injects?: Record<string, unknown>;
  }
): Promise<TicketBasket> {
  const repo = getBasketRepositoryForSave(type, config.basket?.status);

  if (config.injects) {
    repo.setInjects(config.injects);
  }

  // To get the x-sp-uuid response header we need to intercept the response.
  // This value can then be used in consumers of the basket, for example, for analytics.
  const { client } = repo;

  const axiosResponseInterceptor =
    client.interceptors.response.use<TicketBasket>((response) => {
      response.data[X_SP_UUID] = response.headers[X_SP_UUID];
      return response;
    });

  const basketId = config.basket?.id;
  const payload = {
    ...config.basket,
    item_groups,
  };

  try {
    const result = basketId
      ? await repo.update(basketId, payload)
      : await repo.create(payload);
    return result;
  } finally {
    // Eject the interceptor once this request has completed, regardless of status
    client.interceptors.response.eject(axiosResponseInterceptor);
  }
}

/**
 * Expire the given basket
 * Note: This function no longer handles queueing - that is now handled in useBasket.ts
 */
export async function expire(
  type: DataProvider,
  basket: TicketBasket,
  injects?: Record<string, unknown>
): Promise<void> {
  const repo = getBasketRepository(type);

  if (injects) {
    repo.setInjects(injects);
  }

  await repo.expire(basket.id);
}

export const isProductPackageParent = (
  itemGroup: TicketItemGroupType,
  allItemGroups: Array<TicketItemGroupType>
) => {
  const childProductItemGroups = allItemGroups.filter(
    (ig) => ig.parent === itemGroup.id && ig.product
  );
  return (
    itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id &&
    childProductItemGroups.length > 0
  );
};

export const isProductPackageChild = (itemGroup: TicketItemGroupType) => {
  return (
    itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id &&
    itemGroup.parent
  );
};

/*
 * Product package item groups should not be combined with other item groups. Eventually when we generalize
 * any item type package combos this could be extended to support them as well.
 */
export const isNonMergableItemGroup = (
  itemGroup: TicketItemGroupType,
  itemGroups: Array<TicketItemGroupType>
) => {
  return Boolean(
    isProductPackageParent(itemGroup, itemGroups) ||
      isProductPackageChild(itemGroup)
  );
};

export type ToModifyItem = {
  item: TicketType | ProductAttribute | TicketItemGroupType;
  quantity: number;
  pay_what_you_can_price?: string | null;
};

export const filterOutProductPackageItemGroupsThatWillBeModified = (
  toModifyItems: ToModifyItem[],
  itemGroups: Array<TicketItemGroupType>
) => {
  let newItemGroups = itemGroups;

  toModifyItems.forEach(({ item }) => {
    const isItemGroup = "item_type" in item;
    const targetId = isItemGroup ? item.type : item.id;

    const existingItemGroup = itemGroups.find(
      (itemGroup) => itemGroup.type === targetId
    );

    if (
      existingItemGroup &&
      isProductPackageParent(existingItemGroup, newItemGroups)
    ) {
      const parentGroupIds = newItemGroups
        .filter((ig) => ig.type === targetId)
        .map((ig) => ig.id);

      // Remove all item groups that are part of the same product package
      newItemGroups = newItemGroups.filter((itemGroup) => {
        return (
          !parentGroupIds.includes(itemGroup.id) &&
          !parentGroupIds.includes(itemGroup.parent)
        );
      });
    }
  });

  return newItemGroups;
};

/**
 * Consolidates similar item groups together into one item group instead
 * of several and return an array of item groups
 *
 * @param itemGroups - An array of item groups to consolidate
 * @param append - Whether to overwrite quantities of similar item groups or append them
 */
export function consolidate(
  itemGroups: Array<TicketItemGroupType>,
  append = true
): Array<TicketItemGroupType> {
  return itemGroups.reduce<Array<TicketItemGroupType>>((output, _itemGroup) => {
    // store changes in a new variable to prevent reassigning the param
    const newOutput = _.cloneDeep(output);
    const itemGroup = _.cloneDeep(_itemGroup);

    const isNonMergable = isNonMergableItemGroup(itemGroup, itemGroups);
    // if the id already exists in the accumulator then it means we are updating this item group
    // and we need to overwrite it unless is a non-mergable group, in which case we will have to
    // fan it out and create a new item group without an id.
    const exactMatchGroup = output.find((ig) => ig.id === itemGroup.id);

    if (isNonMergable) {
      const quantityDelta =
        (itemGroup?.quantity ?? 0) - (exactMatchGroup?.quantity ?? 0);

      if (exactMatchGroup) {
        if (quantityDelta > 0) {
          return newOutput.concat([
            { ...itemGroup, id: undefined, quantity: quantityDelta },
          ]);
        }
        // this else condition will fall through to line 541
      } else {
        newOutput.push(itemGroup);
        return newOutput;
      }
    }

    // Consolidate renewal memberships.
    if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
      itemGroup.renewal_member
    ) {
      newOutput.push(itemGroup);
      return newOutput;
    }

    // Consolidate gift cards
    if (itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_GIFT_CARD.id) {
      newOutput.push(itemGroup);
    }

    // Consolidate ticket waitlists on seats
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id &&
      isEmpty(itemGroup.tt_seat_permissions) &&
      !isEmpty(itemGroup.waitlist_seat_segment)
    ) {
      const index = findIndexBySeatWaitlistsInItemGroups(
        newOutput,
        itemGroup,
        enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
      );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        // update quantity
        newOutput[index].quantity = itemGroup.quantity ?? 0;
      }
    }
    // Consolidate membership seat permissions
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
      itemGroup.tt_seat_permissions &&
      itemGroup.tt_seat_permissions.length > 0
    ) {
      const index = findIndexBySeatPermissionsInItemGroups(
        newOutput,
        itemGroup,
        enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        // update seat permissions and quantity
        newOutput[index].tt_seat_permissions = newOutput[
          index
        ].tt_seat_permissions?.concat(itemGroup.tt_seat_permissions);
        // IMPORTANT: Also concatenate the permissions array (full seat objects used for UI display)
        // This ensures all assigned seats are shown in the UI when item groups are consolidated
        newOutput[index].permissions =
          newOutput[index].permissions?.concat(itemGroup.permissions ?? []) ??
          [];
        newOutput[index].quantity = append
          ? (newOutput[index].quantity ?? 0) + (itemGroup.quantity ?? 0)
          : itemGroup.quantity ?? 0;
      }
    }

    // Consolidate membership location permissions
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
      itemGroup.tt_location_permissions &&
      itemGroup.tt_location_permissions.length > 0
    ) {
      const index = findIndexByLocPermissionsInItemGroups(
        newOutput,
        itemGroup,
        enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        // IMPORTANT: Concatenate the location_permissions array (full location objects used for UI display)
        // This ensures all assigned locations are shown in the UI when item groups are consolidated
        newOutput[index].location_permissions =
          newOutput[index].location_permissions?.concat(
            itemGroup.location_permissions ?? []
          ) ?? [];
        // only update quantity for locations
        newOutput[index].quantity = itemGroup.quantity ?? 0;
      }
    }

    // Consolidate ticket seat permissions
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id &&
      itemGroup.tt_seat_permissions &&
      itemGroup.tt_seat_permissions.length > 0
    ) {
      const index = findIndexBySeatPermissionsInItemGroups(
        newOutput,
        itemGroup,
        enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
      );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        // update seat permissions and quantity
        newOutput[index].tt_seat_permissions = newOutput[
          index
        ].tt_seat_permissions?.concat(itemGroup.tt_seat_permissions);
        // IMPORTANT: Also concatenate the permissions array (full seat objects used for UI display)
        // This ensures all assigned seats are shown in the UI when item groups are consolidated
        newOutput[index].permissions =
          newOutput[index].permissions?.concat(itemGroup.permissions ?? []) ??
          [];
        newOutput[index].quantity = append
          ? (newOutput[index].quantity ?? 0) + (itemGroup.quantity ?? 0)
          : itemGroup.quantity ?? 0;

        if (
          itemGroup.pay_what_you_can_price ||
          itemGroup.pay_what_you_can_price === null
        ) {
          newOutput[index].pay_what_you_can_price =
            itemGroup.pay_what_you_can_price;
        }
      }
    }

    // Consolidate ticket location permissions
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id &&
      itemGroup.tt_location_permissions &&
      itemGroup.tt_location_permissions.length > 0
    ) {
      const index = findIndexByLocPermissionsInItemGroups(
        newOutput,
        itemGroup,
        enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
      );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        // only update the location permissions if part of a custom package
        if (itemGroup.parent) {
          newOutput[index].tt_location_permissions = newOutput[
            index
          ].tt_location_permissions?.concat(itemGroup.tt_location_permissions);
          // IMPORTANT: Also concatenate the location_permissions array (full location objects used for UI display)
          // This ensures all assigned locations are shown in the UI when item groups are consolidated
          newOutput[index].location_permissions =
            newOutput[index].location_permissions?.concat(
              itemGroup.location_permissions ?? []
            ) ?? [];
        }
        // update location quantity
        newOutput[index].quantity = itemGroup.quantity ?? 0;

        if (
          itemGroup.pay_what_you_can_price ||
          itemGroup.pay_what_you_can_price === null
        ) {
          newOutput[index].pay_what_you_can_price =
            itemGroup.pay_what_you_can_price;
        }
      }
    }

    // Consolidate tickets and products which are handled the same.
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id ||
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id
    ) {
      const index =
        itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
          ? findIndexByTypeInItemGroups(
              newOutput,
              itemGroup,
              itemGroup.item_type
            )
          : findIndexByProductAttributeInItemGroups(
              newOutput,
              itemGroup,
              itemGroup.item_type
            );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        newOutput[index].quantity = append
          ? (newOutput[index].quantity ?? 0) + (itemGroup.quantity ?? 0)
          : itemGroup.quantity ?? 0;

        if (
          itemGroup.pay_what_you_can_price ||
          itemGroup.pay_what_you_can_price === null
        ) {
          newOutput[index].pay_what_you_can_price =
            itemGroup.pay_what_you_can_price;
        }
      }
    }

    // Consolidate memberships without seating permissions
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
    ) {
      const index = findIndexByMembershipLevelInItemGroups(
        newOutput,
        itemGroup
      );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        newOutput[index].quantity = append
          ? (newOutput[index].quantity ?? 0) + (itemGroup.quantity ?? 0)
          : itemGroup.quantity ?? 0;
      }
    }

    // Consolidate donations
    else if (
      itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id
    ) {
      const index = findIndexByTypeInItemGroups(
        newOutput,
        itemGroup,
        itemGroup.item_type
      );

      // if itemgroup not in basket, push to itemgroup array
      if (index === -1) {
        newOutput.push(itemGroup);
      } else {
        newOutput[index].donation_amount = itemGroup.donation_amount ?? 0;
      }
    }

    // Add additional item types as required...

    return newOutput;
  }, []);
}

export function removeSeatPermissions(
  itemGroups: Array<TicketItemGroupType>,
  toRemove: Array<TicketItemGroupType>,
  seatPermissionsToRemove: Array<number>
): Array<TicketItemGroupType> {
  // removing seat permissions requires updating the tt_seat_permissions array
  const mutableItemGroups = itemGroups;
  toRemove.forEach((removeItem) => {
    // find the index of toRemove in mutableItemGroups
    const index = mutableItemGroups.findIndex((originalItem) =>
      isEqual(removeItem, originalItem)
    );
    // check if the index exists
    if (index > -1) {
      // filter seatPermissionsToRemove from tt_seat_permissions
      const newSeatPermissions = mutableItemGroups[
        index
      ].tt_seat_permissions?.filter(
        (p) => !seatPermissionsToRemove.includes(p)
      );
      // if tt_seat_permissions still has items in it, don't remove the itemGroup
      // just update the tt_seat_permissions array
      if (newSeatPermissions && newSeatPermissions.length > 0) {
        mutableItemGroups[index] = {
          ...mutableItemGroups[index],
          quantity: newSeatPermissions.length, // updating quantity is required
          tt_seat_permissions: newSeatPermissions,
        };
      }
      // else tt_seat_permissions is now empty,
      // mark the itemGroup for complete removal
      else {
        mutableItemGroups[index] = {
          ...mutableItemGroups[index],
          id: -100,
        };
      }
    }
  });
  // filter any itemGroups marked for removal
  return mutableItemGroups.filter((itemGroup) => itemGroup.id !== -100);
}

export function filterItemGroups(
  itemGroups: Array<TicketItemGroupType>,
  toRemove: Array<TicketItemGroupType>,
  seatPermissionsToRemove?: Array<number>
): Array<TicketItemGroupType> {
  // removing seat permissions requires special logic
  if (seatPermissionsToRemove) {
    return removeSeatPermissions(itemGroups, toRemove, seatPermissionsToRemove);
  }
  return itemGroups.filter(
    (itemGroup) => !toRemove.some((removable) => isEqual(removable, itemGroup))
  );
}

/**
 * Gets the index of a given item group where it matches an id
 * and item type within the array of items
 */
function findIndexByTypeInItemGroups(
  itemGroups: Array<TicketItemGroupType>,
  { type: whereType, parent: whereParent = null }: TicketItemGroupType,
  whereItemType: enumsTypes.ItemTypesMapIdType
): number {
  return itemGroups.findIndex(({ type, item_type, parent = null }) => {
    return (
      whereItemType === item_type &&
      whereType === type &&
      whereParent === parent
    );
  });
}

/**
 * Gets the index of a given item group where it matches an id
 * and product attribute within the array of items
 */
function findIndexByProductAttributeInItemGroups(
  itemGroups: Array<TicketItemGroupType>,
  { product_attribute: whereProductAttribute, id }: TicketItemGroupType,
  whereItemType: enumsTypes.ItemTypesMapIdType
): number {
  return itemGroups.findIndex((ig) => {
    const { product_attribute, item_type } = ig;
    return (
      whereItemType === item_type &&
      whereProductAttribute === product_attribute &&
      // prevent package item groups from being modified unless the ids match exactly
      (!isProductPackageChild(ig) || ig.id === id)
    );
  });
}

/**
 * Gets the index of a given item group with a waitlisted seat.
 * It matches on the item type (ticket, membership, etc...) and the waitlist_seat_segment object.
 */
function findIndexBySeatWaitlistsInItemGroups(
  itemGroups: Array<TicketItemGroupType>,
  itemGroup: TicketItemGroupType,
  inputType: enumsTypes.ItemTypesMapIdType
): number {
  return itemGroups.findIndex(
    ({ type, membership_level, item_type, waitlist_seat_segment }) => {
      const hasTicket = Boolean(type) && type === itemGroup.type;
      const hasMembership =
        Boolean(membership_level) &&
        membership_level === itemGroup.membership_level;

      const hasSameType = item_type === inputType;
      const hasSameWaitlist = isEqual(
        waitlist_seat_segment,
        itemGroup.waitlist_seat_segment
      );

      return (hasTicket || hasMembership) && hasSameType && hasSameWaitlist;
    }
  );
}

/**
 * Gets the index of a given item group where it matches an id
 * and item type and has a tt_seat_permissions array with at least one item
 */
function findIndexBySeatPermissionsInItemGroups(
  itemGroups: Array<TicketItemGroupType>,
  itemGroup: TicketItemGroupType,
  inputType: enumsTypes.ItemTypesMapIdType
): number {
  return itemGroups.findIndex(
    ({ type, membership_level, item_type, tt_seat_permissions, parent }) => {
      const hasTicket = Boolean(type) && type === itemGroup.type;
      const hasMembership =
        Boolean(membership_level) &&
        membership_level === itemGroup.membership_level;

      // if itemGroup.parent exists, do not include a check for tt_seat_permissions length
      // because the default permissions array is empty for ticket packages
      if (itemGroup.parent) {
        return (
          item_type === inputType &&
          parent === itemGroup.parent &&
          (hasTicket || hasMembership)
        );
      }

      return (
        item_type === inputType &&
        (hasTicket || hasMembership) &&
        tt_seat_permissions &&
        tt_seat_permissions?.length > 0
      );
    }
  );
}

/**
 * Gets the index of a given item group where it matches an id
 * and item type and has a tt_location_permissions array that matches the itemGroup's array.
 *
 * If the tt_location_permissions arrays don't match, a new itemGroup must be created.
 * (tt_location_permissions array can only have 1 id at a time)
 */
function findIndexByLocPermissionsInItemGroups(
  itemGroups: Array<TicketItemGroupType>,
  itemGroup: TicketItemGroupType,
  inputType: enumsTypes.ItemTypesMapIdType
): number {
  return itemGroups.findIndex(
    ({
      type,
      membership_level,
      item_type,
      tt_location_permissions,
      parent,
    }) => {
      const hasTicket = Boolean(type) && type === itemGroup.type;
      const hasMembership =
        Boolean(membership_level) &&
        membership_level === itemGroup.membership_level;

      // if itemGroup.parent exists, do not include a check for tt_location_permissions length
      // because the default permissions array is empty for ticket packages
      if (itemGroup.parent) {
        return (
          item_type === inputType &&
          parent === itemGroup.parent &&
          (hasTicket || hasMembership)
        );
      }

      return (
        item_type === inputType &&
        (hasTicket || hasMembership) &&
        tt_location_permissions &&
        tt_location_permissions?.length > 0 &&
        tt_location_permissions.includes(itemGroup.tt_location_permissions?.[0])
      );
    }
  );
}

export function findIndexByMembershipLevelInItemGroups(
  itemGroups: Array<TicketItemGroupType>,
  itemGroup: TicketItemGroupType
): number {
  return itemGroups.findIndex(
    ({ item_type, membership_level, tt_location_permissions }) => {
      return (
        item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
        membership_level === itemGroup.membership_level &&
        isEmpty(tt_location_permissions) &&
        isEmpty(itemGroup.tt_location_permissions)
      );
    }
  );
}

export function findIndexByMembershipLevelWithLocationPermissions(
  itemGroups: Array<TicketItemGroupType>,
  itemGroup: TicketItemGroupType
): number {
  return itemGroups.findIndex(
    ({ item_type, membership_level, tt_location_permissions }) => {
      return (
        item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
        membership_level === itemGroup.membership_level &&
        !isEmpty(tt_location_permissions) &&
        !isEmpty(itemGroup.tt_location_permissions) &&
        isEqual(tt_location_permissions, itemGroup.tt_location_permissions)
      );
    }
  );
}

export function removeEmptyItemGroups(
  items: Array<TicketItemGroupType>
): Array<TicketItemGroupType> {
  return items.filter(({ quantity }) => quantity);
}

type CustomSubTicketOption = {
  custom_sub_ticket_option: number;
  parent_ticket_type: number;
};

/**
 * Removes items from the basket's selected_custom_sub_ticket_options
 * with a parent ticket type value that match the given ID.
 *
 * @param basketSelections Array of custom package selections on the basket
 * @param typeID Ticket type ID
 */
function removeSelectedCustomSubTicketOptions(
  basketSelections: Array<CustomSubTicketOption>,
  typeID: number
) {
  // eslint-disable-next-line
  _.remove(basketSelections as any, (option: CustomSubTicketOption) => {
    return option.parent_ticket_type === typeID;
  });
}

/**
 * Add selected custom package options to basket
 */
export function getSelectedCustomSubTicketOptions(
  newSelectedPackageOptions: Array<{
    custom_sub_ticket_option: number;
    parent_ticket_type: number;
  }>,
  basketSelections: Array<{
    custom_sub_ticket_option: number;
    parent_ticket_type: number;
  }>
) {
  let selected_custom_sub_ticket_options: Array<TicketBasketCustomSubTicketType> =
    [];

  // Remove options that have the same parent ticket type before pushing
  removeSelectedCustomSubTicketOptions(
    basketSelections,
    newSelectedPackageOptions?.[0]?.parent_ticket_type
  );

  selected_custom_sub_ticket_options = basketSelections?.concat(
    newSelectedPackageOptions
  );
  return selected_custom_sub_ticket_options;
}

/**
 * Returns a given basket repository based on the data provider for the
 * appropriate endpoint.
 *
 * @param type - The data provider
 * @returns The basket repository
 */
export function getBasketRepository(type: DataProvider): HandlesBasketRequests {
  let repo: HandlesBasketRequests;

  switch (type) {
    case DataProvider.Public:
      repo = new PublicBasketRepository();
      break;
    case DataProvider.Venue:
      repo = new VenueBasedTicketBasketRepository();
      break;
    default:
      throw new Error(
        "No basket repository found for the given data provider."
      );
  }

  return repo;
}

export function getBasketHoldsRepository(
  type: DataProvider
):
  | UserBasedTicketBasketHoldRepository
  | VenueBasedTicketBasketHoldsRepository
  | null {
  let repo:
    | UserBasedTicketBasketHoldRepository
    | VenueBasedTicketBasketHoldsRepository
    | null;

  switch (type) {
    case DataProvider.User:
      repo = new UserBasedTicketBasketHoldRepository();
      break;
    case DataProvider.Venue:
      repo = new VenueBasedTicketBasketHoldsRepository();
      break;
    default:
      repo = null;
  }

  return repo;
}

export function getBasketRepositoryForSave(
  type: DataProvider,
  status?: TicketBasketType["status"]
):
  | HandlesBasketRequests
  | UserBasedTicketBasketHoldRepository
  | VenueBasedTicketBasketHoldsRepository {
  let repo = null;
  if (status && status === ITEM_STATUS_OPTIONS.ON_HOLD.id) {
    repo = getBasketHoldsRepository(type);
  }
  // if a holds repository is not found, use the regular basket repository
  if (!repo) {
    repo = getBasketRepository(type);
  }
  return repo;
}

export function getBasketData(type: DataProvider, id: number) {
  const repo = getBasketRepository(type);
  return repo.retrieve(id);
}

/**
 * Check if the given data has the shape of a donation.
 */
export function isDonation(item: unknown): item is Donation {
  const toValidate = item ?? {};

  return (
    Object.prototype.hasOwnProperty.call(toValidate, "id") &&
    Object.prototype.hasOwnProperty.call(toValidate, "refno") &&
    Object.prototype.hasOwnProperty.call(toValidate, "donation_amount")
  );
}

export function isDonationItemType(
  itemType: enumsTypes.ItemTypesMapIdType | undefined
): boolean {
  if (!itemType) return false;

  return itemType === enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id;
}

/**
 * Check if the given data has the shape of a custom package parent.
 */
export function isCustomPackageParent(
  item: unknown
): item is CustomPackageParentType {
  return typeof get(item, "parentTicketType") === "number";
}

/**
 * Check if the given data has the shape of a ticket type.
 */
export function isTicketType(item: unknown): item is TicketType {
  return (
    typeof get(item, "id") === "number" &&
    typeof get(item, "name") === "string" &&
    typeof get(item, "event") === "number"
  );
}

/**
 * Check if the given data has the shape of a membership level.
 */
export function isMembershipLevel(item: unknown): item is MembershipLevel {
  return (
    typeof get(item, "active_members") === "number" ||
    typeof get(item, "membership_level") === "number"
  );
}

/**
 * Check if the item is a membership level.
 */
export function isMembershipLevelItemType(
  itemType: enumsTypes.ItemTypesMapIdType | undefined
): boolean {
  if (!itemType) return false;

  return itemType === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id;
}

/**
 * Check if the given data has the shape of a product attribute.
 */
export function isProductAttribute(item: unknown): item is ProductAttribute {
  return (
    typeof get(item, "id") === "number" &&
    typeof get(item, "name") === "string" &&
    typeof get(item, "inventory") === "number" &&
    Object.prototype.hasOwnProperty.call(item, "sku")
  );
}

export function isProductAttributeItemType(
  itemType: enumsTypes.ItemTypesMapIdType | undefined
): boolean {
  if (!itemType) return false;

  return itemType === enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id;
}

/**
 * Check if the given data has the shape of a seat with a ticket type waitlist.
 */
export function isSeatWaitlist(item: unknown): item is SeatWaitlist {
  return (
    typeof get(item, "itemTypeId") === "number" &&
    typeof get(item, "$data") === "object" &&
    typeof get(item, "mapItemType") === "string" &&
    typeof get(item, "waitlist_seat_segment") === "object"
  );
}

/**
 * Check if the given data has the shape of a ticket type with a seating permission.
 */
export function isSeatPermission(item: unknown): item is SeatPermission {
  // optional fields include minPurchaseLimit and itemGroupParent
  return (
    typeof get(item, "permissionId") === "number" &&
    typeof get(item, "itemTypeId") === "number" &&
    typeof get(item, "$data") === "object" &&
    typeof get(item, "mapItemType") === "string" &&
    typeof get(item, "waitlist_seat_segment") === "undefined"
  );
}

/**
 * Check if the given data has the shape of a ticket type with a location permission.
 */
export function isLocationPermission(
  item: unknown
): item is LocationPermission {
  // optional fields include hold_slug and itemGroupParent
  return (
    typeof get(item, "permissionId") === "number" &&
    typeof get(item, "itemTypeId") === "number" &&
    typeof get(item, "qty") === "number" &&
    typeof get(item, "mapItemType") === "string"
  );
}

/**
 * Check if the given data has the shape of a gift card.
 */
export function isGiftCard(item: unknown): item is GiftCard {
  return (
    typeof get(item, "id") === "number" &&
    typeof get(item, "denomination") === "number" &&
    typeof get(item, "recipient_name") === "string" &&
    typeof get(item, "recipient_email") === "string" &&
    typeof get(item, "custom_message") === "string" &&
    typeof get(item, "notify_on") === "string"
  );
}

export function isGiftCardItemType(
  itemType: enumsTypes.ItemTypesMapIdType | undefined
): boolean {
  if (!itemType) return false;

  return itemType === enums.ITEM_TYPES_MAP.ITEM_TYPE_GIFT_CARD.id;
}

export function isItemGroup(item: unknown): item is ItemGroup {
  return (
    typeof get(item, "id") === "number" &&
    typeof get(item, "quantity") === "number" &&
    typeof get(item, "item_type") === "string"
  );
}

export function isRawBasket(basket: unknown): basket is TicketBasketType {
  // raw basket has fields as snake_case
  // basket has fields as camelCase
  return (
    typeof get(basket, "id") === "number" &&
    typeof get(basket, "payment_venue") === "number" &&
    typeof get(basket, "purchase_source_platform") === "string"
  );
}

/**
 * Merge separated membership level item_groups into one item_group.
 * This is necessary as the backend responds with multiple item_groups for the same membership_level.
 * @param itemGroups - Array of item groups for which to merge memberships
 * @param append - Whether to overwrite quantities of similar item groups or append them.
 *  For example, if there two item groups for the same membership level, when append is true, the quantity
 *  of the membership level will be the sum of the two item groups. When append is false, the quantity of
 *  the membership level will be the quantity of the last matching item group in the array.
 *
 */
export function mergeMemberships(
  itemGroups: Array<TicketItemGroupType>,
  append = true
) {
  return itemGroups.reduce(
    (carry: Array<TicketItemGroupType>, itemGroup: TicketItemGroupType) => {
      // store changes in a new variable to prevent reassigning the param
      const newCarry = [...carry];

      // Only perform actions for this item_group if it is a membership level
      if (
        itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id &&
        !itemGroup.renewal_member
      ) {
        const index = findIndexByMembershipLevelInItemGroups(
          newCarry,
          itemGroup
        );
        const locationPermissionsIndex =
          findIndexByMembershipLevelWithLocationPermissions(
            newCarry,
            itemGroup
          );

        // The itemGroup doesn't exist in newCarry, so push the object as-is
        if (index === -1 && locationPermissionsIndex === -1) {
          newCarry.push(itemGroup);
        }
        // The itemGroup already exists in newCarry AND has location_permissions
        // Update the location's count in the location_permissions array
        else if (locationPermissionsIndex > -1) {
          const item: TicketItemGroupType = newCarry[locationPermissionsIndex];
          let membershipQuantity = item.quantity ?? 0;

          const TTlocationPermissions = itemGroup.tt_location_permissions ?? [];

          const existingLocationPermissions: number =
            item.location_permissions?.reduce(
              (total, locPerm) => locPerm.location.count + total,
              0
            );
          const newLocationPermissions: number =
            itemGroup.location_permissions?.reduce(
              (total, locPerm) => locPerm.location.count + total,
              0
            );
          const countOfLocationPermissions: number =
            existingLocationPermissions + newLocationPermissions;

          // IMPORTANT: Update the location_permissions array (full location objects used for UI display)
          // This ensures all assigned locations are shown in the UI when item groups are merged
          // Note: the count is updated to reflect the new total (there is only ever 1 item in this array)
          const uniqueLocationPermissions = item.location_permissions?.map(
            (p) => {
              return {
                ...p,
                location: {
                  ...p.location,
                  count: countOfLocationPermissions,
                },
              };
            }
          );

          /* If append is true and there are location items, location permissions with different IDs stay on separate itemGroups.
           * Location permissions with the same ID are collapsed such that location_permissions.location.count reflects the new total.
           *
           * eg.
           * 1. { membership_level: 1, tt_location_permissions: [1], location_permissions: {...location: {...count: 1 }} }
           * 2. { membership_level: 1, tt_location_permissions: [1], location_permissions: {...location: {...count: 1 }} }
           * 3. { membership_level: 1, tt_location_permissions: [2], ... }
           *
           * Permissions with different IDs stay on different itemGroups.
           * In the example above, the itemGroups with tt_location_permissions = [1] and [2] cannot be merged into the same group.
           *
           * The itemGroups with tt_location_permissions = [1] can be collapsed into one itemGroup where location_permissions.location.count = 2.
           * We can assume that location_permissions.location.count is the new membership quantity.
           */
          if (countOfLocationPermissions > 0 && append) {
            membershipQuantity = countOfLocationPermissions;
          } else if (append) {
            // if there are no seating items and append is true, just add to the quantity
            membershipQuantity += itemGroup.quantity ?? 0;
          } else {
            // if append is false, just use the quantity of the itemGroup
            membershipQuantity = itemGroup.quantity ?? 0;
          }

          newCarry[locationPermissionsIndex] = {
            ...item,
            quantity: membershipQuantity,
            tt_location_permissions: uniq(TTlocationPermissions),
            location_permissions: uniqueLocationPermissions,
          };
        }
        // The itemGroup already exists in newCarry
        // If there are incoming seat permissions, add them to the newCarry.permissions array
        // Else just update the membership's quantity
        else if (index > -1) {
          const item: TicketItemGroupType = newCarry[index];
          let membershipQuantity = item.quantity ?? 0;

          const TTseatPermissions =
            item.tt_seat_permissions?.concat(
              itemGroup.tt_seat_permissions ?? []
            ) ?? [];

          // IMPORTANT: Also concatenate the permissions array (full seat objects used for UI display)
          // This ensures all assigned seats are shown in the UI when item groups are merged
          const uniqueSeatPermissions =
            item.permissions?.concat(itemGroup.permissions ?? []) ?? [];

          const countOfSeatPermissions: number = TTseatPermissions.length;

          /* If append is true and there are seating items,
           * each seat permission will be collapsed into a single array on newCarry.
           *
           * eg.
           * 1. { membership_level: 1, tt_seat_permissions: [1] }
           * 2. { membership_level: 1, tt_seat_permissions: [2] }
           * 3. { membership_level: 1, tt_seat_permissions: [3] }
           *
           * These permissions will be combined to form seatPermissions = [1, 2, 3]
           * We can assume that countOfSeatPermissions (the seatPermissions array length) is the new membership quantity
           */
          if (countOfSeatPermissions > 0 && append) {
            membershipQuantity = countOfSeatPermissions;
          } else if (append) {
            // if there are no seating items and append is true, just add to the quantity
            membershipQuantity += itemGroup.quantity ?? 0;
          } else {
            // if append is false, just use the quantity of the itemGroup
            membershipQuantity = itemGroup.quantity ?? 0;
          }

          newCarry[index] = {
            ...item,
            quantity: membershipQuantity,
            tt_seat_permissions: uniq(TTseatPermissions),
            permissions: uniqueSeatPermissions,
          };
        }
      }
      // Other item groups should be included as is
      else {
        newCarry.push(itemGroup);
      }

      return newCarry;
    },
    []
  );
}

/**
 * Checks if item groups have the required seat or location permissions.
 * This is used to determine whether to persist basket changes - we only persist when all groups will have
 * complete permissions after the modification.
 *
 * @param itemGroups - All item groups in the basket
 * @param excludeGroupIds - Optional group IDs to exclude from the check (default: none)
 * @returns true if all non-excluded groups have seat or location permissions equal to quantity
 */
export function checkItemGroupsHavePermissions(
  itemGroups: Array<TicketItemGroupType>,
  excludeGroupIds: Set<number | undefined> | number | undefined = new Set()
): boolean {
  const idsToExclude =
    excludeGroupIds instanceof Set
      ? excludeGroupIds
      : new Set([excludeGroupIds]);

  return itemGroups
    .filter((group) => !idsToExclude.has(group.id))
    .every(
      (group) =>
        (group.tt_seat_permissions &&
          group.tt_seat_permissions.length === group.quantity) ||
        (group.tt_location_permissions &&
          group.tt_location_permissions.length === group.quantity)
    );
}
